import { FC, HTMLAttributes, memo } from 'react';
import cn from 'classnames';

import {
  ProductCharacteristicData,
  Parameter,
  ProductParameterGroupData,
  GroupPlace,
  ProductParameterValueData,
  UnitData,
  SizeData,
} from '@Types/Product';
import Specification from './elements/Specification';
import Sizes from './elements/Sizes';
import styles from './Characteristics.module.css';

export interface CharacteristicsProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  groups: ProductParameterGroupData[];
  parameterValues: ProductParameterValueData[];
  parameters: Parameter[];
  units: UnitData[];
}

export type ProductGroups = Record<GroupPlace, ProductParameterGroupData[]>;

const Characteristics: FC<CharacteristicsProps> = (props) => {
  const { groups, parameterValues, parameters, units, className, ...restProps } = props;

  return (
    <div {...restProps} className={className}>
      {groups.map((group, index) => {
        const currentParameters = parameters.filter((targetParameter) => {
          return targetParameter.groupId === group.id;
        });

        switch (group.theme) {
          case 'sizes': {
            const sizes: SizeData[] = [];

            currentParameters.forEach((parameter) => {
              const parameterValue = parameterValues.find((pv) => {
                return pv.parameterId === parameter.id;
              });
              const unit = units.find((u) => u.id === parameterValue.unitId);
              const value = parameterValue.value.map((item) => parseFloat(item)).join(', ');

              sizes.push({
                title: parameter.title,
                value: `${value} ${unit.title}`,
              });
            });

            return (
              <Specification
                key={`${group.theme}_${index}`}
                className={styles.sizeGroup}
                title={group.title}
              >
                <Sizes sizes={sizes} />
              </Specification>
            );
          }
          case 'default':
          case 'hardness': {
            const characteristics: ProductCharacteristicData[] = [];
            currentParameters.forEach((parameter) => {
              const value = parameterValues.find((v) => v.parameterId === parameter.id);
              const unit = units.find((u) => u.id === value.unitId);

              characteristics.push({
                name: parameter.title,
                value: value.value,
                icon: value.icon,
                unit: unit && unit.title ? unit.title : '',
              });
            });

            return (
              <div
                key={`${group.theme}_${index}`}
                className={cn(styles.parameter, styles.defaultGroup)}
              >
                <div className={cn(styles.type, 'Specifications')}>{`${characteristics.map(
                  (item) => item.name,
                )}:`}</div>
                {characteristics.map(
                  (item) =>
                    item.icon && (
                      <img key={item.icon} className={styles.icon} src={item.icon} alt='' />
                    ),
                )}
                <div className={styles.characteristicValue}>
                  {characteristics.map((item) =>
                    item.value
                      .map((value) => (item.unit ? `${value} ${item.unit}` : value))
                      .join(', '),
                  )}
                </div>
              </div>
            );
          }
          default: {
            return null;
          }
        }
      })}
    </div>
  );
};

export default memo(Characteristics);
